import { useId, useState } from "react";
import { Label } from "@/components/ui/label";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { useWilayah, wilayahUrl, type District, type Region } from "@/lib/wilayah";

export interface RegionValue {
  province: string;
  city: string;
  district: string;
  postalCode: string;
}

const toOptions = (regions: Region[] | null): ComboboxOption[] =>
  (regions ?? []).map((r) => ({ value: r.code, label: r.name }));

/**
 * Provinsi → Kota/Kabupaten → Kecamatan → Kode pos, each a searchable dropdown
 * that opens once the one before it is chosen. Nothing is typed: the postal
 * code is picked from the ones that kecamatan actually has, with the kelurahan
 * each covers, because a mistyped postal code is a shipping quote to the wrong
 * city.
 *
 * Emits region names (what the address stores) and drives the cascade with
 * Kemendagri codes internally.
 */
export function RegionPicker({
  value,
  onChange,
  disabled,
}: {
  value: RegionValue;
  onChange: (next: RegionValue) => void;
  disabled?: boolean;
}) {
  const uid = useId();
  const [provinceCode, setProvinceCode] = useState<string | null>(null);
  const [regencyCode, setRegencyCode] = useState<string | null>(null);
  const [districtCode, setDistrictCode] = useState<string | null>(null);

  const provinces = useWilayah<Region[]>(wilayahUrl.provinces());
  const regencies = useWilayah<Region[]>(provinceCode ? wilayahUrl.regencies(provinceCode) : null);
  const districts = useWilayah<District[]>(regencyCode ? wilayahUrl.districts(regencyCode) : null);

  const district = districts.data?.find((d) => d.code === districtCode) ?? null;
  const postcodeOptions: ComboboxOption[] = (district?.postcodes ?? []).map((p) => ({
    value: p.code,
    label: p.code,
    hint: p.villages.join(", "),
  }));

  const loadFailed = provinces.error || regencies.error || districts.error;

  return (
    <>
      <PickerField id={`${uid}-province`} label="Provinsi">
        <Combobox
          id={`${uid}-province`}
          required
          disabled={disabled}
          value={provinceCode ?? ""}
          options={toOptions(provinces.data)}
          loading={provinces.loading}
          placeholder="Pilih provinsi"
          searchPlaceholder="Cari provinsi…"
          onChange={(code) => {
            setProvinceCode(code);
            setRegencyCode(null);
            setDistrictCode(null);
            const name = provinces.data?.find((p) => p.code === code)?.name ?? "";
            onChange({ province: name, city: "", district: "", postalCode: "" });
          }}
        />
      </PickerField>
      <PickerField id={`${uid}-city`} label="Kota / Kabupaten">
        <Combobox
          id={`${uid}-city`}
          required
          disabled={disabled || !provinceCode}
          value={regencyCode ?? ""}
          options={toOptions(regencies.data)}
          loading={regencies.loading}
          placeholder={provinceCode ? "Pilih kota/kabupaten" : "Pilih provinsi dulu"}
          searchPlaceholder="Cari kota/kabupaten…"
          onChange={(code) => {
            setRegencyCode(code);
            setDistrictCode(null);
            const name = regencies.data?.find((r) => r.code === code)?.name ?? "";
            onChange({ ...value, city: name, district: "", postalCode: "" });
          }}
        />
      </PickerField>
      <PickerField id={`${uid}-district`} label="Kecamatan">
        <Combobox
          id={`${uid}-district`}
          required
          disabled={disabled || !regencyCode}
          value={districtCode ?? ""}
          options={toOptions(districts.data)}
          loading={districts.loading}
          placeholder={regencyCode ? "Pilih kecamatan" : "Pilih kota dulu"}
          searchPlaceholder="Cari kecamatan…"
          onChange={(code) => {
            setDistrictCode(code);
            const d = districts.data?.find((x) => x.code === code);
            // One postal code is no choice at all, so it is made for them.
            const only = d?.postcodes.length === 1 ? (d.postcodes[0]?.code ?? "") : "";
            onChange({ ...value, district: d?.name ?? "", postalCode: only });
          }}
        />
      </PickerField>
      <PickerField id={`${uid}-postal`} label="Kode pos">
        <Combobox
          id={`${uid}-postal`}
          required
          disabled={disabled || !district}
          value={value.postalCode}
          options={postcodeOptions}
          placeholder={district ? "Pilih kode pos" : "Pilih kecamatan dulu"}
          searchPlaceholder="Cari kode pos atau kelurahan…"
          onChange={(code) => onChange({ ...value, postalCode: code })}
        />
      </PickerField>
      {loadFailed && (
        <p className="text-sm text-destructive sm:col-span-2">
          Daftar wilayah gagal dimuat. Periksa koneksi lalu muat ulang halaman.
        </p>
      )}
    </>
  );
}

function PickerField({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label htmlFor={id} className="mb-1.5 block">
        {label}
        <span className="ml-0.5 text-destructive">*</span>
      </Label>
      {children}
    </div>
  );
}
